import { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { User, Calendar, Brain, FileText, Sparkles, Target } from '../../components/icons';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { isUserCoach, checkCoachAIAccess } from '../../lib/auth';
import { ResultsGraph } from '../../components/ResultsGraph';
import { HarmonicGraph } from '../../components/HarmonicGraph';
import { AIAnalysisPanel } from '../../components/ai/AIAnalysisPanel';
import { CoachNotesPanel } from '../../components/ai-coach/CoachNotesPanel';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import { CoachProgressView } from '../../components/coach/CoachProgressView';
import type { AssessmentResult } from '../../types/results';

export function RespondentResults() {
  const navigate = useNavigate();
  const { userId } = useParams<{ userId: string }>();
  const { user, isLoading: authLoading } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [results, setResults] = useState<AssessmentResult[]>([]);
  const [selectedVersion, setSelectedVersion] = useState(0);
  const [respondentName, setRespondentName] = useState('');
  const [hasAccess, setHasAccess] = useState(false);
  const [hasAIAccess, setHasAIAccess] = useState(false);
  const [coachId, setCoachId] = useState<string | null>(null);
  const [respondentId, setRespondentId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'profile' | 'progress'>('profile');

  useEffect(() => {
    if (authLoading) return;
    if (!user || !userId) {
      navigate('/login');
      return;
    }

    const init = async () => {
      try {
        const isCoach = await isUserCoach(user.id);
        if (!isCoach) {
          navigate('/login');
          return;
        }

        const { data: coach } = await supabase
          .from('coaches')
          .select('id')
          .eq('user_id', user.id)
          .maybeSingle();

        if (!coach) {
          navigate('/login');
          return;
        }

        const { data: respondent } = await supabase
          .from('respondents')
          .select('id, user_id, users!respondents_user_id_fkey(full_name, email)')
          .eq('user_id', userId)
          .eq('coach_id', coach.id)
          .maybeSingle();

        if (!respondent) {
          navigate('/coach/respondents');
          return;
        }

        setRespondentName((respondent.users as any)?.full_name || (respondent.users as any)?.email || 'Respondent');
        setHasAccess(true);
        setCoachId(coach.id);
        setRespondentId(respondent.id);

        const aiAccess = await checkCoachAIAccess(user.id);
        setHasAIAccess(aiAccess);

        const { data: resultsData } = await supabase
          .from('results')
          .select('*')
          .eq('user_id', userId)
          .order('completed_at', { ascending: false });

        const mapped = (resultsData || []).map(r => ({
          id: r.id,
          userId: r.user_id,
          coachId: r.coach_id || undefined,
          partnerId: r.partner_id || undefined,
          scores: r.scores as any,
          harmonicScores: r.harmonic_scores as any,
          completedAt: new Date(r.completed_at || Date.now()),
          aiAnalysis: r.ai_analysis || undefined,
          coachAiAnalysis: r.coach_ai_analysis || undefined,
          version: r.version || 1,
          originalResultId: r.original_result_id || undefined
        })) as AssessmentResult[];

        setResults(mapped);
      } catch (err) {
        console.error('Error loading results:', err);
      } finally {
        setIsLoading(false);
      }
    };

    init();
  }, [user, authLoading, navigate, userId]);

  const handleAnalysisUpdate = (analysis: string) => {
    setResults(prev => prev.map((r, i) =>
      i === selectedVersion ? { ...r, coachAiAnalysis: analysis } : r
    ));
  };

  if (authLoading || isLoading) {
    return <LoadingSpinner message="Loading results..." />;
  }

  if (!hasAccess || results.length === 0) {
    return (
      <div className="min-h-screen freud-bg py-12 px-4">
        <div className="max-w-2xl mx-auto text-center">
          <div className="freud-card-elevated p-8">
            <FileText className="w-16 h-16 text-cream-400 mx-auto mb-4" />
            <h2 className="freud-heading-xl text-chocolate-900 mb-2">No Results Found</h2>
            <p className="text-chocolate-500 mb-6">
              This respondent hasn't completed an assessment yet.
            </p>
            <Link
              to="/coach/respondents"
              className="freud-btn-primary inline-flex items-center gap-2"
            >
              Back to Respondents
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const selectedResult = results[selectedVersion];

  return (
    <div className="min-h-screen freud-bg">
      <header className="bg-white/80 backdrop-blur-sm border-b border-cream-300 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 bg-sage-100 rounded-xl flex items-center justify-center">
                <User className="w-6 h-6 text-sage-600" />
              </div>
              <div>
                <h1 className="freud-heading-xl text-chocolate-900">{respondentName}</h1>
                <div className="flex items-center gap-2 text-sm text-chocolate-500">
                  <Calendar className="w-4 h-4" />
                  Completed: {selectedResult.completedAt.toLocaleDateString()}
                </div>
              </div>
            </div>

            <div className="flex items-center gap-3">
              {results.length > 1 && activeTab === 'profile' && (
                <select
                  value={selectedVersion}
                  onChange={(e) => setSelectedVersion(Number(e.target.value))}
                  className="freud-input w-auto"
                >
                  {results.map((_, index) => (
                    <option key={index} value={index}>
                      Version {results.length - index}
                      {index === 0 ? ' (Latest)' : ''}
                    </option>
                  ))}
                </select>
              )}

              {/* Tab switcher */}
              <div className="flex bg-cream-100 rounded-xl p-1 gap-1">
                <button
                  onClick={() => setActiveTab('profile')}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${activeTab === 'profile' ? 'bg-white text-chocolate-900 shadow-sm' : 'text-chocolate-500 hover:text-chocolate-700'}`}
                >
                  <Brain className="w-4 h-4" />
                  Profile
                </button>
                <button
                  onClick={() => setActiveTab('progress')}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${activeTab === 'progress' ? 'bg-white text-chocolate-900 shadow-sm' : 'text-chocolate-500 hover:text-chocolate-700'}`}
                >
                  <Target className="w-4 h-4" />
                  Progress
                </button>
              </div>

              <Link
                to={`/coach/proof/${userId}/reports`}
                className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium border border-amber-200 bg-amber-50 text-amber-700 hover:bg-amber-100 transition-colors"
              >
                <FileText className="w-4 h-4" />
                Reports
              </Link>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 md:px-8 py-8">
        {activeTab === 'profile' ? (
          <div className="space-y-8">
            <div className="grid lg:grid-cols-2 gap-8">
              <div className="freud-card-elevated p-8">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 bg-sage-100 rounded-xl flex items-center justify-center">
                    <Brain className="w-5 h-5 text-sage-600" />
                  </div>
                  <h2 className="freud-heading-xl text-chocolate-900">
                    Characteristic Profile
                  </h2>
                </div>
                <ResultsGraph scores={selectedResult.scores} />
              </div>

              <div className="freud-card-elevated p-8">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 bg-coral-100 rounded-xl flex items-center justify-center">
                    <Sparkles className="w-5 h-5 text-coral-600" />
                  </div>
                  <h2 className="freud-heading-xl text-chocolate-900">
                    Harmonic Scale Profile
                  </h2>
                </div>
                <HarmonicGraph scores={selectedResult.harmonicScores} />
              </div>
            </div>

            {hasAIAccess && (
              <AIAnalysisPanel
                analysis={selectedResult.coachAiAnalysis}
                scores={selectedResult.scores}
                harmonicScores={selectedResult.harmonicScores}
                resultId={selectedResult.id}
                showRegenerate
                respondentName={respondentName}
                storageField="coach_ai_analysis"
                onAnalysisUpdate={handleAnalysisUpdate}
              />
            )}

            {coachId && respondentId && (
              <CoachNotesPanel coachId={coachId} respondentId={respondentId} />
            )}
          </div>
        ) : (
          userId && <CoachProgressView userId={userId} />
        )}
      </main>
    </div>
  );
}
